import { useLocation, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { reveal } from "../../components/motion/presets";
import HeaderProgressBar from "./components/HeaderProgressBar";
import { TRANSPORTER, TRANSPORTERS } from "../../Helper/partnerLabels";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import CustomerConversation from "./components/CustomerConversation";
import AirlineConversation from "./components/AirlineConversation";
import ShipperConversation from "./components/ShipperConversation";
import TransportConversation from "./components/TransportConversation";
import FinalQuoteCostTable from "../../components/FinalQuoteCostTable";
import { useLaneUnread } from "./useLaneUnread";
import QuoteDetails from "./components/QuoteDetails";
import StageChecklistDropdown from "./components/StageChecklistDropdown";
import {
  falseFlagQuote,
  getAQuoteById,
  getQuoteChecklistDetails,
  parseShipmentFromInput,
  fetchEmailThread,
} from "../../Api";
import toast from "react-hot-toast";
import {
  clearAiParseLock,
  mapThreadMessageForPicker,
  stripHtmlToText,
  setAiParseLock,
  isAiParseLocked,
} from "../../Helper/aiEmailParseHelpers";
import { applyAiParsedToQuote, restoreFieldFromSnapshot } from "../../Helper/applyAiParsedToQuote";
import { countOperatorEditsSinceAiParse } from "../../Helper/countOperatorEditsSinceAiParse";
import AiParseOverlay from "../../components/shared/AiParseOverlay";
import AiRerunConfirmDialog from "../../components/shared/AiRerunConfirmDialog";
import { clearHighlight } from "../../redux/threadSlice";
import { showSwal } from "../../Helper/ShowAlert";
import FullscreenModal from "../../components/shared/FullscreenModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faChevronLeft,
  faChevronDown,
  faChevronUp,
  faCalculator,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faUser,
  faPlane,
  faTruck,
  faBuilding,
  faArrowRight,
  faEnvelope,
  faBullseye,
  faFileExport,
  faStamp,
  faEnvelopeCircleCheck,
  faTableColumns,
} from "@fortawesome/free-solid-svg-icons";
import { Plane as PlaneGlyph } from "lucide-react";
import { handleRole } from "../../Helper/handleRoleHelper";
import StickyNote from "../../components/StickyNotes";
import { ShimmerBox } from "../../components/shared/SkeletonComponents";
import AnimatedLoader from "../../motionKit/AnimatedLoader";
import "./quoteWorkspace.css";
import PanelBoundary from "../../components/shared/PanelBoundary";
import {
  COMPACT_DESKTOP_MAX_PX,
  isCompactDesktopViewport,
} from "../../constants/layoutBreakpoints";

let ghostData = {};

export const PureOpens = {
  CustomerConversation: false,
  AirlineConversation: false,
  ShipperConversation: false,
  TransportConversation: false,
  FinalQuoteCostTable: false,
  StageChecklist: false,
  QuickNavigation: true,
  QuoteDetails: true,
  DocumentsCreation: false,
};

const PANEL_ORDER = [
  "CustomerConversation",
  "AirlineConversation",
  "TransportConversation",
  "ShipperConversation",
];

// Mirrors the settled 3-region workspace (left lane rail / center conversation
// / right shipment inspector) so nothing snaps in when the quote lands. Kept in
// sync with the grid template + region structure below.
const QuoteDetailsSkeleton = () => (
  <div className="relative flex min-h-0 flex-1 flex-col">
    {/* Branded workspace loader: the quote-lifecycle pipeline, centered over
        the layout skeleton so the wait feels like FK, not a blank grid. */}
    <div className="pointer-events-none absolute inset-x-0 top-1/3 z-10 flex flex-col items-center">
      <AnimatedLoader variant="pipeline" size={130} label="Loading quote workspace" />
      <p className="mt-1 text-[12px] font-medium text-slate-500 dark:text-slate-400">
        Loading quote workspace…
      </p>
    </div>
    {/* Slim header pill row — mirrors the collapsed quote-overview status rail */}
    <div className="shrink-0 rounded-[20px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#fbfcff_100%)] dark:bg-none p-3 shadow-card ring-1 ring-white/60 dark:border-[oklch(0.3_0.02_264/0.7)] dark:bg-[oklch(0.225_0.026_264)] dark:ring-white/5 sm:rounded-[24px] sm:p-4 xl:rounded-[28px] xl:p-5">
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <ShimmerBox width="180px" height="24px" borderRadius="9999px" />
          <ShimmerBox width="150px" height="24px" borderRadius="9999px" />
          <ShimmerBox width="200px" height="24px" borderRadius="9999px" />
        </div>
        <ShimmerBox width="96px" height="26px" borderRadius="9999px" />
      </div>
    </div>

    <div className="mt-3 grid min-h-0 min-w-0 flex-1 grid-cols-1 gap-3 sm:mt-4 sm:gap-4 xl:grid-cols-[236px_minmax(0,1fr)_minmax(0,400px)] xl:items-stretch">
      {/* Left — workspace lane rail (~5 blocks) */}
      <aside className="min-h-0 min-w-0" aria-hidden>
        <div className="rounded-[20px] border border-slate-200/80 bg-[linear-gradient(180deg,#ffffff_0%,#fbfcff_100%)] dark:bg-none p-2.5 shadow-card ring-1 ring-white/60 dark:border-[oklch(0.3_0.02_264/0.7)] dark:bg-[oklch(0.225_0.026_264)] dark:ring-white/5">
          <ShimmerBox width="45%" height="12px" className="mb-3 ml-1" />
          <div className="space-y-1.5">
            {[...Array(5)].map((_, idx) => (
              <ShimmerBox key={idx} width="100%" height="48px" borderRadius="12px" />
            ))}
          </div>
        </div>
      </aside>

      {/* Center — conversation column (~3 message cards) */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col space-y-3 sm:space-y-4" aria-hidden>
        {[...Array(3)].map((_, idx) => (
          <div
            key={idx}
            className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-card dark:border-[oklch(0.3_0.02_264/0.6)] dark:bg-[oklch(0.225_0.026_264)]"
          >
            <div className="flex items-center gap-3">
              <ShimmerBox width="40px" height="40px" borderRadius="9999px" />
              <div className="flex-1 space-y-2">
                <ShimmerBox width="55%" height="14px" />
                <ShimmerBox width="35%" height="12px" />
              </div>
            </div>
            <div className="mt-3 space-y-2">
              <ShimmerBox width="100%" height="14px" />
              <ShimmerBox width="92%" height="14px" />
              <ShimmerBox width="78%" height="14px" />
            </div>
          </div>
        ))}
      </div>

      {/* Right — shipment inspector (2-col grid of ~8 field rows) */}
      <aside className="min-h-0 min-w-0" aria-hidden>
        <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-card dark:border-[oklch(0.3_0.02_264/0.6)] dark:bg-[oklch(0.225_0.026_264)]">
          <ShimmerBox width="50%" height="16px" className="mb-4" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[...Array(8)].map((_, idx) => (
              <div key={idx} className="space-y-1.5">
                <ShimmerBox width="30%" height="11px" />
                <ShimmerBox width="100%" height="36px" borderRadius="10px" />
              </div>
            ))}
          </div>
        </div>
      </aside>
    </div>
  </div>
);

/* LaneMotion lived here: a pulsing ring on Customer, three blinking nodes with
   a packet flying between them on Airlines, a bar driving across Transport, and
   two attention ripples on Shipper. Four independent loops inside a 32px icon,
   on a rail you read down while working. Removed on review — "the animations
   here are a little distracting, I wouldn't have any anywhere; I would maybe use
   a different tint of a colour to highlight that these are buttons."

   Colour does that job now, and does it while standing still: each control and
   each lane tile carries a wash of its own accent, so the row reads as pressable
   without anything moving. */

// Lane-level action affordance from the design: Airlines carries a "request
// carrier rates" radar, Final Cost carries a "generate quote" export glyph.
// Rendered as a visual badge inside the (already clickable) lane button — a
// click bubbles to the lane and activates it, so it stays valid, non-nested
// markup while matching the mockup's per-lane action icons.
function LaneAction({ laneKey }) {
  if (laneKey === "AirlineConversation") {
    return (
      <span
        aria-hidden
        title="Request carrier rates"
        className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[9px]"
        style={{ background: "var(--blue-soft)", color: "var(--blue)" }}
      >
        <FontAwesomeIcon icon={faBullseye} className="text-[11px]" />
      </span>
    );
  }
  if (laneKey === "FinalQuoteCostTable") {
    return (
      <span
        aria-hidden
        title="Generate quote PDF"
        className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[9px]"
        style={{ background: "var(--violet-soft)", color: "var(--violet)" }}
      >
        <FontAwesomeIcon icon={faFileExport} className="text-[11px]" />
      </span>
    );
  }
  return null;
}

function ConversationLaneButton({ item, active, onClick, iconOnly, tourId, unread = 0 }) {
  // Tighter, more professional lane card: single row for label + status,
  // subtitle as a slim muted line, chevron reveal on hover.
  const availabilityClasses = item.available
    ? "border-slate-200/70 bg-white/95 text-slate-900 hover:border-slate-300 hover:bg-white hover:shadow-[0_6px_16px_-12px_rgba(15,23,42,0.18)] dark:border-[oklch(0.33_0.022_264/0.6)] dark:bg-[oklch(0.225_0.026_264/0.72)] dark:text-slate-100 dark:hover:border-[oklch(0.4_0.02_264)]"
    : "border-slate-200/60 bg-slate-50/70 text-slate-500 hover:border-slate-300 hover:bg-slate-50 dark:border-[oklch(0.3_0.02_264/0.6)] dark:bg-[oklch(0.2_0.021_264/0.6)] dark:text-slate-400 dark:hover:border-[oklch(0.33_0.022_264)]";

  const statusDot = item.available ? "bg-emerald-500" : "bg-amber-500";
  const statusText = item.available
    ? "text-emerald-700 dark:text-emerald-300"
    : "text-amber-700 dark:text-amber-300";

  // The design tints each lane's glyph by its identity, not a flat slate: the
  // customer lane is blue; the carrier (Airlines), haulage (Transport) and
  // shipper lanes are amber; Final Cost stays neutral. Uses the .fk-qw tokens
  // so it adapts in dark mode.
  const laneAccentColor =
    item.key === "CustomerConversation"
      ? "var(--blue)"
      : item.key === "AirlineConversation" ||
          item.key === "TransportConversation" ||
          item.key === "ShipperConversation"
        ? "var(--amber)"
        : undefined;
  /* Same identity, at wash strength, for the tile behind the glyph. Final Cost
     carries no accent, so it falls back to the neutral line token rather than no
     background at all — every tile needs a surface, or one lane looks unfinished
     next to four that are tinted. */
  const laneSoftColor =
    laneAccentColor === "var(--blue)"
      ? "var(--blue-soft)"
      : laneAccentColor === "var(--amber)"
        ? "var(--amber-soft)"
        : "var(--line)";

  if (iconOnly) {
    const baseIcon = item.available
      ? "border-slate-200/80 bg-white/90 text-slate-700 hover:border-slate-300 hover:bg-white hover:shadow-[0_6px_14px_-8px_rgba(15,23,42,0.20)]"
      : "border-slate-200/70 bg-slate-50/80 text-slate-500 hover:border-slate-300 hover:bg-slate-100";
    return (
      <button
        type="button"
        onClick={onClick}
        data-tour={tourId || undefined}
        title={`${item.label} — ${item.status}`}
        aria-label={`${item.label}, ${item.status}${unread > 0 ? `, ${unread} new` : ""}`}
        aria-current={active ? "true" : undefined}
        className={`relative mx-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border text-sm transition-all duration-200 ${
          active
            ? "border-fk-accent/60 bg-gradient-to-b from-blue-50 to-blue-100/70 text-fk-accent ring-1 ring-fk-accent/20 shadow-[0_10px_24px_-10px_rgba(37,99,235,0.35),0_1px_0_rgba(255,255,255,0.9)_inset]"
            : baseIcon
        }`}
      >
        <FontAwesomeIcon icon={item.icon} />
        {unread > 0 ? (
          /* Collapsed rail still has to answer "which lane?", so the unread
             count replaces the status dot rather than sitting beside it. */
          <span className="absolute -right-1 -top-1 grid h-4 min-w-[1rem] place-items-center rounded-full bg-fk-accent px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white dark:ring-[oklch(0.225_0.026_264)]">
            {unread > 9 ? "9+" : unread}
          </span>
        ) : (
          <span
            className={`absolute bottom-1 right-1 h-1.5 w-1.5 rounded-full ring-2 ring-white ${statusDot}`}
            aria-hidden
          />
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      data-tour={tourId || undefined}
      style={active ? { background: "var(--blue-soft)", borderColor: "var(--blue-line)" } : undefined}
      className={`group relative w-full overflow-hidden rounded-2xl border px-2.5 py-2 text-left transition-all duration-200 ${
        active
          ? "text-slate-950 ring-1 ring-fk-accent/15 shadow-[0_10px_24px_-14px_rgba(37,99,235,0.30),0_1px_0_rgba(255,255,255,0.9)_inset]"
          : availabilityClasses
      }`}
    >
      {/* Accent stripe for active state */}
      {active && (
        <span
          aria-hidden
          className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-fk-accent/70"
        />
      )}
      <div className="flex items-center gap-2.5">
        <div
          className={`relative flex h-8 w-8 shrink-0 items-center justify-center overflow-visible rounded-xl border text-[13px] transition-colors ${
            active
              ? "border-blue-200/80 bg-white text-fk-accent shadow-[0_2px_6px_-2px_rgba(37,99,235,0.25)] dark:border-blue-500/30 dark:bg-[oklch(0.26_0.03_264)]"
              : item.available
                ? "border-transparent text-slate-600 group-hover:text-fk-accent dark:text-slate-300"
                : "border-slate-200/70 bg-white text-slate-400 dark:border-[oklch(0.3_0.02_264/0.6)] dark:bg-[oklch(0.24_0.024_264)] dark:text-slate-500"
          }`}
          /* The tile carries a wash of its lane's own accent — blue for the
             customer, amber for the three supplier lanes — which is what the
             per-lane animation inside it used to be doing, only louder. The
             active lane keeps the plain white tile so it still stands out
             against four tinted ones. */
          style={
            !active && item.available && laneSoftColor
              ? { background: laneSoftColor }
              : undefined
          }
        >
          <FontAwesomeIcon
            icon={item.icon}
            style={laneAccentColor ? { color: laneAccentColor } : undefined}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate text-[12.5px] font-semibold leading-tight tracking-tight text-slate-900 dark:text-slate-100">
                {item.label}
              </span>
              {unread > 0 ? (
                /* The whole point of this rail while you are deep in another
                   conversation: which lane just received something. */
                <span
                  className="grid h-4 min-w-[1rem] shrink-0 place-items-center rounded-full bg-fk-accent px-1 text-[10px] font-bold leading-none text-white"
                  title={`${unread} new message${unread === 1 ? "" : "s"}`}
                >
                  {unread > 9 ? "9+" : unread}
                </span>
              ) : null}
            </span>
            {item.key === "AirlineConversation" ||
            item.key === "FinalQuoteCostTable" ? (
              <LaneAction laneKey={item.key} />
            ) : (
              <FontAwesomeIcon
                icon={faChevronRight}
                className={`shrink-0 text-[9px] transition-all ${
                  active
                    ? "text-fk-accent translate-x-0.5"
                    : "text-slate-300 opacity-0 group-hover:translate-x-0.5 group-hover:text-slate-500 group-hover:opacity-100"
                }`}
              />
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5">
            <span
              className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${statusDot}`}
              aria-hidden
            />
            <span
              className={`truncate text-[10.5px] font-semibold uppercase tracking-[0.11em] ${statusText}`}
            >
              {item.status}
            </span>
            {item.detail ? (
              <>
                <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                  ·
                </span>
                <span className="truncate text-[11px] text-slate-500 dark:text-slate-400">
                  {item.detail}
                </span>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </button>
  );
}

export default function QuoteAllDetails() {
  const { id } = useParams();
  const location = useLocation();
  const { state } = location;
  const navigate = useNavigate();
  const loginToken = useSelector((state) => state.reducer.userToken);
  const userLoggedIn = useSelector((state) => state.reducer.userLoggedIn);
  const dispatch = useDispatch();

  const [quote, setQuote] = useState(null);
  const [currentStage, setCurrentStage] = useState(null);
  const [C_EMAIL, setC_EMAIL] = useState("");
  const [S_EMAIL, setS_EMAIL] = useState("");
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  // Rate saves only need FinalQuoteCostTable to re-fetch (not the full quote).
  const [fqctTrigger, setFqctTrigger] = useState(0);
  const [defaultOpen] = useState({
    ...PureOpens,
    ...(state?.defaultOpen ? state.defaultOpen : {}),
  });
  const [itemRefs, setItemRefs] = useState([]);
  const tableRefs = useRef([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [input, setInput] = useState("");
  const [flagLoading, setFlagLoading] = useState(false);

  const [aiParsing, setAiParsing] = useState(false);
  const [reparseConfirmOpen, setReparseConfirmOpen] = useState(false);
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);
  const [aiChangedFields, setAiChangedFields] = useState([]);
  const [aiConfidence, setAiConfidence] = useState({});
  const [aiParseMeta, setAiParseMeta] = useState({ cached: false, cachedAt: null });
  const [containerInspectorEpoch, setContainerInspectorEpoch] = useState(0);
  const aiParseHandledRef = useRef(false);
  const autoParseHandledRef = useRef(false);
  const autoParseFromNotifRef = useRef(false);
  // Latches once the notification deep-link thread has been focused, so a
  // later refetch (e.g. saving a transporter/agent) that produces a new
  // `quote` reference does NOT re-run the focus effect and snap the
  // conversation panel back to the notification's entityType.
  const notifFocusHandledRef = useRef(false);
  const aiLastRunRef = useRef({ emailSubject: "", emailBody: "", messageId: null });
  const preParseSnapshotRef = useRef(null);
  // Mirror `quote` into a ref so callbacks whose deps don't include `quote`
  // (notably performAiParse) can read the latest value synchronously. We
  // previously relied on the setState updater function to compute the AI
  // merge AND set a sibling local `mergeResult`, then read it on the next
  // line — but React's eager-update path can defer the updater, leaving
  // `mergeResult` undefined and silently skipping every post-parse side
  // effect (containerInspectorEpoch bump, summary panel, toast).
  const quoteRef = useRef(null);

  const [fullscreenPanel, setFullscreenPanel] = useState(null);
  /** Icon-only shipment inspector rail (lg+); replaces fully hiding the panel. */
  const [inspectorIconOnly, setInspectorIconOnly] = useState(false);
  /** Icon-only workspace lane rail (lg+); default on for compact desktop, user can expand. */
  const [lanesIconOnly, setLanesIconOnly] = useState(false);
  const [lgMediaUp, setLgMediaUp] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );
  /** When false, stages + workspace controls hide so the conversation column gets more vertical space.
   *  Defaults to expanded: the stage rail is how an operator sees where a shipment
   *  actually is, and behind a collapsed panel labelled "Overview" nobody found it. */
  const [quoteOverviewExpanded, setQuoteOverviewExpanded] = useState(true);


  useEffect(() => {
    aiParseHandledRef.current = false;
    autoParseHandledRef.current = false;
    setAiSummaryOpen(false);
    setAiChangedFields([]);
    setAiConfidence({});
    setAiParseMeta({ cached: false, cachedAt: null });
    preParseSnapshotRef.current = null;
    setContainerInspectorEpoch(0);
    // Drop any attached-table refs / forced templates carried over from the
    // previous quote. Without this, navigating from Quote A (after attaching
    // a finalised table) to Quote B via a notification leaves the old DOM
    // refs in openPanel.customer.missingFields, so Quote B's reply composer
    // shows Quote A's table under "NON-EDITABLE TABLE ATTACHMENTS HERE:".
    //
    // Reset to FRESH panel objects rather than closing everything: the
    // active conversation (customer by default) stays OPEN on every quote,
    // so the page never lands with all threads collapsed. A fresh `{}`
    // still discards the stale refs the comment above describes.
    {
      const tabToPanelKey = {
        CustomerConversation: "customer",
        AirlineConversation: "airline",
        ShipperConversation: "shipper",
        TransportConversation: "transport",
      };
      const activeKey =
        tabToPanelKey[state?.activeConversationTab || "CustomerConversation"] ||
        "customer";
      setOpenPanel({
        customer: activeKey === "customer" ? {} : false,
        airline: activeKey === "airline" ? {} : false,
        shipper: activeKey === "shipper" ? {} : false,
        transport: activeKey === "transport" ? {} : false,
      });
    }
  }, [id]);

  // Re-arm the one-shot notification focus whenever the deep-link target
  // changes (new quote, or a new thread/entity carried in location.state).
  useEffect(() => {
    notifFocusHandledRef.current = false;
  }, [id, state?.threadId, state?.entityType]);

  useEffect(() => {
    quoteRef.current = quote;
  }, [quote]);

  useEffect(() => {
    if (location.state?.parseWithAi) {
      aiParseHandledRef.current = false;
    }
  }, [location.state?.parseWithAi, id]);

  const [activeConversationTab, setActiveConversationTab] = useState(
    state?.activeConversationTab || "CustomerConversation"
  );


  useLayoutEffect(() => {
    if (isCompactDesktopViewport()) {
      setInspectorIconOnly(true);
      setLanesIconOnly(true);
    }
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const sync = () => setLgMediaUp(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    let prevWide = window.innerWidth > COMPACT_DESKTOP_MAX_PX;
    const onResize = () => {
      const wide = window.innerWidth > COMPACT_DESKTOP_MAX_PX;
      if (wide !== prevWide && !wide) {
        setInspectorIconOnly(true);
        setLanesIconOnly(true);
      }
      prevWide = wide;
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const lanesCompact = lanesIconOnly && lgMediaUp;

  /** Three columns from xl (1280px); below that stack lanes / thread / inspector for small desktops.
   *
   * The inspector gets a second, larger cap from 2xl. It is the densest column
   * on the page — labelled field / value pairs, several of which truncate at
   * 400px — so on a screen with width to spare it is the one that should take
   * it, rather than handing every spare pixel to the conversation column, which
   * is already the widest and mostly whitespace inside. The lanes column keeps
   * its 236px: it holds five fixed-width cards and would only gain more air.
   */
  const mainWorkspaceGridClass = useMemo(() => {
    if (inspectorIconOnly && lanesCompact) {
      return "xl:grid-cols-[minmax(0,72px)_minmax(0,1fr)_minmax(0,72px)]";
    }
    if (inspectorIconOnly && !lanesCompact) {
      return "xl:grid-cols-[236px_minmax(0,1fr)_minmax(0,72px)]";
    }
    if (!inspectorIconOnly && lanesCompact) {
      return "xl:grid-cols-[minmax(0,72px)_minmax(0,1fr)_minmax(0,400px)] 2xl:grid-cols-[minmax(0,72px)_minmax(0,1fr)_minmax(0,480px)]";
    }
    return "xl:grid-cols-[236px_minmax(0,1fr)_minmax(0,400px)] 2xl:grid-cols-[236px_minmax(0,1fr)_minmax(0,480px)]";
  }, [inspectorIconOnly, lanesCompact]);

  const editedSinceParseCount = useMemo(
    () => (quote ? countOperatorEditsSinceAiParse(quote) : 0),
    [quote]
  );

  // QuickNavigation data
  const navItemsData = [
    {
      label: "Request Rates Airlines",
      onClick: (quote) => navigate(`/${handleRole(userLoggedIn?.role)}/${quote?._id}/rate-request`, { state: { containerId: quote?.containerDetails?._id, quoteId: quote?._id, receivedInInbox: quote?.receivedInInbox } }),
      disabled: (quote) => !quote?.containerDetails?._id
    },
    {
      label: `Request ${TRANSPORTER} Rates`,
      onClick: (quote) => navigate(`/${handleRole(userLoggedIn?.role)}/${quote?._id}/agent-rates`, { state: { backTo: window.location.pathname, quote: quote } }),
      disabled: () => false
    },
    {
      label: "Generate Documents",
      onClick: (quote) => navigate(`/${handleRole(userLoggedIn?.role)}/${quote?._id}/generate-documents`, { state: { backTo: window.location.pathname, quote: quote } }),
      disabled: (quote) => !quote?.shipperDetails?._id
    },
    {
      label: "Freight Calculator",
      onClick: (quote) => navigate(`/${handleRole(userLoggedIn?.role)}/${quote?._id}/calculator2`, { state: { backTo: window.location.pathname, quote } }),
      disabled: (quote) => !quote,
    }
  ];

  // Compact glyph + short label for the always-visible shortcut strip beneath
  // the lanes (parallel to navItemsData, same order) so the operator reaches
  // rate-requests / documents / calculator without expanding Overview.
  /* `soft` is what makes these read as buttons without moving: a wash of each
     control's own accent behind it, instead of four white rectangles that look
     like labels. The -soft tokens are already redefined under the dark scope in
     quoteWorkspace.css, so this stays correct in both themes. */
  const workspaceShortcutMeta = [
    { short: "Airline rates", icon: faBullseye, color: "var(--blue)", soft: "var(--blue-soft)" },
    { short: `${TRANSPORTER} rates`, icon: faTruck, color: "var(--amber)", soft: "var(--amber-soft)" },
    { short: "Documents", icon: faFileExport, color: "var(--violet)", soft: "var(--violet-soft)" },
    { short: "Calculator", icon: faCalculator, color: "var(--emerald)", soft: "var(--emerald-soft)" },
  ];

  const lastUpdatedThreadId = useSelector(
    (state) => state.threads.lastUpdated?.threadId
  );

  const [openPanel, setOpenPanel] = useState(() => {
    const initialPanels = {
      customer: defaultOpen["CustomerConversation"],
      airline: defaultOpen["AirlineConversation"],
      shipper: defaultOpen["ShipperConversation"],
      transport: defaultOpen["TransportConversation"],
    };

    const tabToPanelKey = {
      CustomerConversation: "customer",
      AirlineConversation: "airline",
      ShipperConversation: "shipper",
      TransportConversation: "transport",
    };

    const initialPanelKey = tabToPanelKey[state?.activeConversationTab || "CustomerConversation"];
    if (initialPanels[initialPanelKey] === false) {
      initialPanels[initialPanelKey] = {};
    }

    return initialPanels;
  });
  const [panelTriggerKey, setPanelTriggerKey] = useState(0);
  const openFS = useCallback((name) => {
    setFullscreenPanel(prev => prev === name ? null : name);
  }, []);
  const closeFS = () => setFullscreenPanel(null);

  // The cost table owns its own fullscreen portal; routing it through the host's
  // FullscreenModal as well mounted a SECOND table (and a second fetch) on top
  // of the inline one, so the host deliberately does not drive it.
  const finalQuoteDisabled = useMemo(() =>
    ((!quote?.my_saved_airlines ||
      quote?.my_saved_airlines?.length == 0) ||
      (!quote?.my_saved_agents ||
        quote?.my_saved_agents?.length == 0)) &&
    `Airlines & ${TRANSPORTERS} Details are required to create Final Quote Cost Table`,
    [quote?.my_saved_airlines, quote?.my_saved_agents]
  );
  const goPrev = () => {
    const idx = PANEL_ORDER.indexOf(fullscreenPanel);
    if (idx > -1)
      setFullscreenPanel(
        PANEL_ORDER[(idx - 1 + PANEL_ORDER.length) % PANEL_ORDER.length]
      );
  };
  const goNext = () => {
    const idx = PANEL_ORDER.indexOf(fullscreenPanel);
    if (idx > -1) setFullscreenPanel(PANEL_ORDER[(idx + 1) % PANEL_ORDER.length]);
  };

  const activateConversation = useCallback((conversationKey, bumpTrigger = true) => {
    setActiveConversationTab(conversationKey);
    setOpenPanel((prev) => ({
      ...prev,
      customer:
        conversationKey === "CustomerConversation"
          ? (prev.customer && typeof prev.customer === "object" ? prev.customer : {})
          : false,
      airline:
        conversationKey === "AirlineConversation"
          ? (prev.airline && typeof prev.airline === "object" ? prev.airline : {})
          : false,
      shipper:
        conversationKey === "ShipperConversation"
          ? (prev.shipper && typeof prev.shipper === "object" ? prev.shipper : {})
          : false,
      transport:
        conversationKey === "TransportConversation"
          ? (prev.transport && typeof prev.transport === "object" ? prev.transport : {})
          : false,
    }));
    if (bumpTrigger) {
      setPanelTriggerKey((prev) => prev + 1);
    }
  }, []);

  const resolveParseMessages = useCallback(
    async (q) => {
      if (!q?.gmailThreadId || !loginToken || !q?.receivedInInbox) return [];
      try {
        const res = await fetchEmailThread({
          token: loginToken,
          threadId: q.gmailThreadId,
          userEmail: q.receivedInInbox,
          role: userLoggedIn?.role,
          skipInlineImages: true,
        });
        if (!res?.messages?.length) return [];
        return res.messages.map(mapThreadMessageForPicker);
      } catch {
        return [];
      }
    },
    [loginToken, userLoggedIn?.role]
  );

  const performAiParse = useCallback(
    async (rawText, messageIdForApi, force, attachment) => {
      /* An attachment IS the input when the body says only "details attached" —
         which is exactly the case this supports, so an empty body is fine. */
      if (!rawText?.trim() && !attachment) {
        toast.error("No email text to parse.");
        clearAiParseLock(id);
        return;
      }
      if (!id || !loginToken || !userLoggedIn?.role) return;

      try {
        const res = await parseShipmentFromInput({
          loginToken,
          role: userLoggedIn.role,
          text: (rawText || "").trim().slice(0, 6000),
          quoteId: id,
          messageId: messageIdForApi || undefined,
          force,
          attachmentIndex: attachment?.index,
          attachmentFilename: attachment?.filename,
          attachmentThreadId: attachment?.threadId,
        });

        if (res?.success === false) {
          toast.error(res?.message || "AI parse failed.");
          clearAiParseLock(id);
          return;
        }

        const parsed = res?.data;
        if (parsed == null || (typeof parsed === "object" && !Object.keys(parsed).length)) {
          toast.error(
            res?.message ||
              (attachment
                ? `Nothing could be extracted from ${attachment.filename || "that attachment"}.`
                : "Nothing could be extracted from this email.")
          );
          clearAiParseLock(id);
          return;
        }

        // Compute the merge synchronously so every side effect below has a
        // concrete `mergeResult` to read. We deliberately do NOT use
        // `setQuote(prev => ...)` and stash `mergeResult` inside the updater
        // — React's eager-update path can defer the updater (e.g. when an
        // earlier setState in this async flow has lanes queued), leaving
        // mergeResult undefined and silently skipping containerInspectorEpoch
        // (so QuoteDetails' field-sync effect never fires → user has to
        // refresh to see AI-filled fields).
        // `quoteRef.current` mirrors the latest `quote` state via a useEffect
        // above; this callback's deps don't include `quote`, so a plain
        // closure read here would be stale.
        const currentQuote = quoteRef.current;
        if (!currentQuote) {
          clearAiParseLock(id);
          return;
        }
        const mergeResult = applyAiParsedToQuote(currentQuote, parsed);
        // Mirror what the backend does in parseShipmentC (sets aiParsedAt
        // server-side after a successful extraction). Without this the local
        // quote state never reflects "this quote has been AI-parsed", so the
        // Re-parse button stays hidden until a refetch.
        const parsedAtIso = new Date().toISOString();
        setQuote({ ...mergeResult.nextQuote, aiParsedAt: parsedAtIso });
        setAiChangedFields(mergeResult.changedFields);
        setAiConfidence(mergeResult.confidence);
        setAiParseMeta({ cached: !!res?.cached, cachedAt: res?.cachedAt ?? null });
        const hasChanges = mergeResult.changedFields.length > 0;
        setAiSummaryOpen(hasChanges);
        setContainerInspectorEpoch((e) => e + 1);

        if (res?.notFreight) {
          toast("This may not be a freight request.", { icon: "ℹ️" });
        }
        if (!hasChanges && !res?.notFreight) {
          toast("Already up to date — no new fields to fill", { icon: "✓" });
        } else if (hasChanges) {
          if (autoParseFromNotifRef.current) {
            toast.success(
              "New query — AI auto-filled the form. Review before submitting."
            );
            autoParseFromNotifRef.current = false;
          } else if (res?.cached) {
            toast.success("Loaded cached AI parse. Click Re-run to refresh.");
          } else {
            toast.success("Form auto-filled from email. Review before submitting.");
          }
        }
      } catch (e) {
        console.error(e);
        const st = e?.response?.status;
        const errCode = e?.response?.data?.error;
        if (st === 429 || errCode === "AI_DAILY_TOKEN_CAP") {
          if (errCode === "AI_DAILY_TOKEN_CAP") {
            toast.error(
              "AI parse budget reached for today — please retry tomorrow or parse manually."
            );
          } else {
            toast.error("AI is busy. Try again in a minute.");
          }
        } else {
          toast.error("Could not reach AI parse. Try again.");
        }
      }
    },
    [id, loginToken, userLoggedIn?.role]
  );

  const runAiParse = useCallback(
    async ({ messageId: messageIdParam = null, force = true } = {}) => {
      if (!quote?._id || !loginToken || !userLoggedIn?.role) {
        autoParseFromNotifRef.current = false;
        return;
      }
      if (isAiParseLocked(quote._id)) {
        autoParseFromNotifRef.current = false;
        toast("A parse is already in progress for this quote.", { icon: "ℹ️" });
        return;
      }
      setAiParseLock(quote._id);
      // Show the overlay synchronously the moment a parse is committed, so
      // the loader is visible while we still have to fetch the Gmail thread
      // to pick the right message. Re-parse otherwise looked dead between
      // dismissing the confirm modal and the API request actually firing.
      setAiParsing(true);
      try {
        const emailSubject = (quote?.subject || "").trim();
        const mapped = await resolveParseMessages(quote);
        const chosen = messageIdParam
          ? mapped.find((m) => String(m.id) === String(messageIdParam)) || null
          : mapped[0] || null;
        const emailBody =
          (chosen?.bodyText || "").trim() ||
          stripHtmlToText(quote?.bodyHtml || quote?.body || "").trim() ||
          "";
        if (!emailSubject && !emailBody) {
          toast.error("This email has no content to parse.");
          return;
        }
        const resolvedMid = chosen?.id != null ? String(chosen.id) : null;
        aiLastRunRef.current = {
          emailSubject,
          emailBody,
          messageId: resolvedMid,
        };
        preParseSnapshotRef.current = JSON.parse(JSON.stringify(quote));
        const rawText = `${emailSubject}\n\n${emailBody}`
          .trim()
          .slice(0, 6000);
        await performAiParse(rawText, resolvedMid, force);
      } finally {
        setAiParsing(false);
        clearAiParseLock(quote._id);
        autoParseFromNotifRef.current = false;
      }
    },
    [quote, loginToken, userLoggedIn?.role, resolveParseMessages, performAiParse]
  );

  /** Quote-detail tour: mount the right panels before the overlay measures targets (avoids wrong spotlight / centered card). */
  useEffect(() => {
    /** Reveal the header's collapsed blocks and centre-column lanes as the tour progresses.
     *  Indexes match quoteDetailTour.js — update both sides together. */
    const applyForStep = (step) => {
      // Steps that need the overview expanded (stage bar + workspace controls visible)
      if (step === 1 || step === 2) setQuoteOverviewExpanded(true);
      // Steps that need the right-hand inspector visible
      if (step === 3) setInspectorIconOnly(false);
      // Steps that need the lane rail expanded so labels are visible
      if (step === 4 || step === 5 || step === 7 || step === 10 || step === 12) {
        setLanesIconOnly(false);
      }
      // Lane switching for each workflow phase
      if (step === 5 || step === 6) activateConversation("CustomerConversation", false);
      else if (step >= 7 && step <= 9) activateConversation("AirlineConversation", false);
      else if (step === 10 || step === 11) activateConversation("TransportConversation", false);
      else if (step === 12 || step === 13) activateConversation("FinalQuoteCostTable", false);
    };
    const onTourStep = (e) => {
      const { tourId, step } = e.detail || {};
      if (tourId !== "quote-detail" || typeof step !== "number") return;
      applyForStep(step);
    };
    const onTourStart = (e) => {
      if (e.detail?.tourId !== "quote-detail") return;
      applyForStep(0);
    };
    window.addEventListener("fk:tour:step", onTourStep);
    window.addEventListener("fk:tour:start", onTourStart);
    return () => {
      window.removeEventListener("fk:tour:step", onTourStep);
      window.removeEventListener("fk:tour:start", onTourStart);
    };
  }, [activateConversation]);

  const triggerPanel = (
    panelName,
    missingFields,
    selectTemplate,
    dataForCalculation,
    multiple = false,
    fieldName = "",
    selectedId = "",
    openRates
  ) => {
    ghostData[panelName] = {
      missingFields,
      selectTemplate,
    };
    setOpenPanel((prev) => {
      let arrayOTables = multiple ? missingFields.map((n) => itemRefs.includes(n) ? tableRefs.current[n] : false).filter(x => x != false) : []
      return ({
        ...prev,
        [panelName]: {
          missingFields: multiple ? arrayOTables : itemRefs.includes(missingFields)
            ? tableRefs.current[missingFields]
            : missingFields,
          selectTemplate,
          dataForCalculation,
          openRates,
          ...(fieldName !== "" && { [fieldName]: selectedId })
        },
      })
    });
    setPanelTriggerKey((prev) => prev + 1);

    // Switch to the appropriate conversation tab when opening customer or shipper panel
    if (panelName === "customer") {
      activateConversation("CustomerConversation", false);
    } else if (panelName === "shipper") {
      activateConversation("ShipperConversation", false);
    } else if (panelName === "airline") {
      activateConversation("AirlineConversation", false);
    } else if (panelName === "transport") {
      activateConversation("TransportConversation", false);
    }
  };

  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const res = await getAQuoteById({ loginToken, role: userLoggedIn?.role, id });
        setQuote(res?.quotes[0]);
      } catch (err) {
        console.error("Error fetching quote:", err);
      }
    };
    if (id) fetchQuotes();
  }, [id, loginToken, S_EMAIL, refetchTrigger, location.pathname]);

  useEffect(() => {
    if (!quote?._id) return;
    const st = location.state;
    if (!st?.scrollToQuoteConversation) return;
    requestAnimationFrame(() => {
      document
        .querySelector('[data-tour="qd-conversation"]')
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    const { scrollToQuoteConversation: _sc, ...rest } = st;
    navigate(location.pathname, {
      replace: true,
      state: Object.keys(rest).length ? rest : undefined,
    });
  }, [quote?._id, location.state?.scrollToQuoteConversation, navigate, location.pathname]);

  useEffect(() => {
    const st = location.state;
    if (!st?.parseWithAi || !id || !loginToken || !userLoggedIn?.role) return;
    if (!quote?._id || String(quote._id) !== String(id)) return;
    if (aiParseHandledRef.current) return;

    const emailSubject = st.emailSubject ?? "";
    const emailBody = st.emailBody ?? "";
    const messageId = st.messageId ?? null;
    const {
      parseWithAi: _parseWithAi,
      emailSubject: _emailSubject,
      emailBody: _emailBody,
      messageId: _messageId,
      aiSource: _aiSource,
      ...restState
    } = st;
    const cleanState = Object.keys(restState).length ? restState : undefined;
    navigate(location.pathname, { replace: true, state: cleanState });

    aiParseHandledRef.current = true;
    aiLastRunRef.current = { emailSubject, emailBody, messageId };
    preParseSnapshotRef.current = JSON.parse(JSON.stringify(quote));

    const rawText = `${emailSubject}\n\n${emailBody}`.trim().slice(0, 6000);
    if (!rawText) {
      toast.error("No email text to parse.");
      clearAiParseLock(id);
      return;
    }

    let cancelled = false;
    setAiParsing(true);
    setAiParseLock(id);

    (async () => {
      try {
        await performAiParse(rawText, messageId, false);
      } finally {
        // Always clear the loader and lock; the `cancelled` flag only guards
        // form-state writes inside performAiParse, not the spinner itself.
        setAiParsing(false);
        clearAiParseLock(id);
      }
    })();

    // Cleanup: just mark cancelled so the IIFE knows we no longer care if it
    // unmounts mid-parse. Crucially, do NOT call setAiParsing(false) here —
    // the navigate(replace) above changes location.state synchronously, which
    // would trigger this cleanup and dismiss the overlay before it ever
    // renders. The IIFE's finally block is the only place that flips the
    // loader off, on actual completion.
    return () => {
      cancelled = true;
    };
  }, [
    quote?._id,
    id,
    loginToken,
    userLoggedIn?.role,
    location.state,
    navigate,
    location.pathname,
    performAiParse,
  ]);

  useEffect(() => {
    if (!quote?._id) return;
    if (String(quote._id) !== String(id)) return;
    if (autoParseHandledRef.current) return;
    if (aiParseHandledRef.current) return;
    if (!location.state?.fromNotification) return;
    if (quote.type !== "query") return;
    if (quote.aiParsedAt) return;
    if (quote.source === "manual") return;
    if (isAiParseLocked(quote._id)) return;

    autoParseHandledRef.current = true;

    const { fromNotification: _fn, ...restState } = location.state || {};
    navigate(location.pathname, {
      replace: true,
      state: Object.keys(restState).length ? restState : undefined,
    });

    autoParseFromNotifRef.current = true;
    runAiParse({ force: false });
  }, [
    quote?._id,
    quote?.type,
    quote?.aiParsedAt,
    quote?.source,
    id,
    location.state,
    location.pathname,
    navigate,
    runAiParse,
  ]);

  /* An operator picked an attachment in the thread. Runs the SAME extraction and
     merge as a body parse — only the source differs — so the changed-field
     summary, confidence and undo all behave identically. */
  useEffect(() => {
    const onParseAttachment = async (e) => {
      const { quoteId: evtQuoteId, filename, threadId } = e.detail || {};
      if (!filename) return;
      if (evtQuoteId && String(evtQuoteId) !== String(id)) return;
      if (aiParsing) return;

      setAiParsing(true);
      try {
        preParseSnapshotRef.current = quoteRef.current
          ? JSON.parse(JSON.stringify(quoteRef.current))
          : null;
        toast.loading(`Reading ${filename}…`, { id: "parse-attachment" });
        await performAiParse("", null, true, { filename, threadId });
      } finally {
        toast.dismiss("parse-attachment");
        setAiParsing(false);
        clearAiParseLock(id);
      }
    };
    window.addEventListener("fk:parse-attachment", onParseAttachment);
    return () =>
      window.removeEventListener("fk:parse-attachment", onParseAttachment);
  }, [id, aiParsing, performAiParse]);

  const handleAiParseRerun = useCallback(() => {
    setReparseConfirmOpen(true);
  }, []);
  /* One button, two moods: a never-parsed quote runs the AI parse directly
     ("Parse with AI"); an already-parsed one goes through the re-run
     confirm so operators don't clobber fields by accident. */
  const handleAiParseClick = useCallback(() => {
    if (quote?.aiParsedAt) {
      setReparseConfirmOpen(true);
    } else {
      runAiParse({ force: false });
    }
  }, [quote?.aiParsedAt, runAiParse]);
  const confirmReparse = useCallback(() => {
    setReparseConfirmOpen(false);
    runAiParse({ force: true });
  }, [runAiParse]);

  const handleAiUndoField = useCallback(
    (fieldId) => {
      setQuote((prev) => restoreFieldFromSnapshot(prev, preParseSnapshotRef.current, fieldId));
      setAiChangedFields((rows) => rows.filter((r) => r.id !== fieldId));
      setContainerInspectorEpoch((e) => e + 1);
    },
    []
  );

  const handleAiUndoAll = useCallback(() => {
    const snap = preParseSnapshotRef.current;
    if (snap) setQuote(JSON.parse(JSON.stringify(snap)));
    setAiChangedFields([]);
    setAiConfidence({});
    setAiSummaryOpen(false);
    setContainerInspectorEpoch((e) => e + 1);
  }, []);

  // Auto-select airline/agent from notification threadId
  useEffect(() => {
    if (!quote || !state?.threadId || !state?.entityType) return;
    // One-shot per notification target: without this guard, every refetch
    // (e.g. saving a transporter) produces a new `quote` reference that
    // re-fires this effect and clobbers the operator's panel selection by
    // re-activating the notification's conversation tab.
    if (notifFocusHandledRef.current) return;

    const { threadId, entityType } = state;

    if (entityType === "airline" && quote.my_saved_airlines) {
      // Find airline with matching emailThreadId
      const matchingAirline = quote.my_saved_airlines.find(
        (airline) => airline.emailThreadId === threadId
      );

      if (matchingAirline && matchingAirline.airlineId) {
        // Update openPanel to auto-select this airline
        setOpenPanel((prev) => ({
          ...prev,
          airline: {
            selectedAirlineId: matchingAirline.airlineId,
            missingFields: prev.airline?.missingFields || {},
            selectTemplate: prev.airline?.selectTemplate || null,
            dataForCalculation: prev.airline?.dataForCalculation || {},
          },
        }));
        setPanelTriggerKey((prev) => prev + 1);
        activateConversation("AirlineConversation", false);
        notifFocusHandledRef.current = true;
      }
    } else if ((entityType === "agent" || entityType === "transporter") && quote.my_saved_agents) {
      // Find agent with matching emailThreadId
      const matchingAgent = quote.my_saved_agents.find(
        (agent) => agent.emailThreadId === threadId
      );

      if (matchingAgent && matchingAgent.agentId) {
        // Update openPanel to auto-select this agent
        setOpenPanel((prev) => ({
          ...prev,
          transport: {
            selectedAgentId: matchingAgent.agentId,
            missingFields: prev.transport?.missingFields || {},
            selectTemplate: prev.transport?.selectTemplate || null,
            dataForCalculation: prev.transport?.dataForCalculation || {},
          },
        }));
        setPanelTriggerKey((prev) => prev + 1);
        activateConversation("TransportConversation", false);
        notifFocusHandledRef.current = true;
      }
    }
  }, [quote, state?.threadId, state?.entityType, activateConversation]);

  // Fetch checklist data on page load to set current stage
  useEffect(() => {
    const fetchChecklist = async () => {
      if (!id || !loginToken || !userLoggedIn?.role) return;

      try {
        const response = await getQuoteChecklistDetails({
          token: loginToken,
          quoteId: id,
          role: userLoggedIn.role
        });
        if (response.status === 200 && response.checklist?.currentStage) {
          setCurrentStage(response.checklist.currentStage);
        }
      } catch (err) {
        console.error("Error fetching checklist details:", err);
      }
    };
    fetchChecklist();
  }, [id, loginToken, userLoggedIn?.role]);

  useEffect(() => {
    if (lastUpdatedThreadId) {
      const timer = setTimeout(() => {
        dispatch(clearHighlight());
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [lastUpdatedThreadId, dispatch]);

  // Handle navigation from GenerateDocuments for portal email sending
  useEffect(() => {
    if (state?.type && state?.purpose) {
      // Trigger the appropriate conversation panel based on type
      if (state.type === 'shipper') {
        // Open shipper conversation with the template
        triggerPanel("shipper", state.shipper_magic_link, state.purpose);
      } else if (state.type === 'transporter') {
        // Open transport conversation with the template
        triggerPanel("transport", state.hauler_magic_link, state.purpose);
      }
    }
  }, [state]);

  // Open conversation accordion when defaultOpen is set from notification navigation
  useEffect(() => {
    if (!state?.defaultOpen) return;

    // Check which conversation types should be open
    const shouldOpenCustomer = state.defaultOpen["CustomerConversation"];
    const shouldOpenAirline = state.defaultOpen["AirlineConversation"];
    const shouldOpenShipper = state.defaultOpen["ShipperConversation"];
    const shouldOpenTransport = state.defaultOpen["TransportConversation"];

    // Update openPanel to open the accordions
    setOpenPanel((prev) => {
      const updated = { ...prev };

      if (shouldOpenCustomer) {
        // Ensure customer panel is open (use object to maintain structure)
        updated.customer = prev.customer && typeof prev.customer === 'object' ? prev.customer : {};
      }
      if (shouldOpenAirline) {
        // If it's an airline ID (string), use it; otherwise ensure it's an object
        if (typeof shouldOpenAirline === 'string') {
          updated.airline = { selectedAirlineId: shouldOpenAirline };
        } else if (!prev.airline || typeof prev.airline !== 'object') {
          updated.airline = {};
        }
      }
      if (shouldOpenShipper) {
        // Ensure shipper panel is open (use object to maintain structure)
        updated.shipper = prev.shipper && typeof prev.shipper === 'object' ? prev.shipper : {};
      }
      if (shouldOpenTransport) {
        // If it's an agent ID (string), use it; otherwise ensure it's an object
        if (typeof shouldOpenTransport === 'string') {
          updated.transport = { selectedAgentId: shouldOpenTransport };
        } else if (!prev.transport || typeof prev.transport !== 'object') {
          updated.transport = {};
        }
      }
      return updated;
    });

    if (shouldOpenCustomer) {
      activateConversation("CustomerConversation", false);
    } else if (shouldOpenAirline) {
      activateConversation("AirlineConversation", false);
    } else if (shouldOpenShipper) {
      activateConversation("ShipperConversation", false);
    } else if (shouldOpenTransport) {
      activateConversation("TransportConversation", false);
    }

    // Trigger panel update to ensure accordion opens
    setPanelTriggerKey((prev) => prev + 1);
  }, [state?.defaultOpen, activateConversation]);

  const renderPanel = (name, isFullscreen = false) => {
    if (!quote) return null;
    switch (name) {
      case "CustomerConversation":
        return (
          <CustomerConversation
            threadId={quote?.gmailThreadId}
            openPanel={openPanel}
            setOpenPanel={setOpenPanel}
            panelTriggerKey={panelTriggerKey}
            disabled={!quote?.gmailThreadId && "Customer Conversation not found"}
            TO_EMAIL={C_EMAIL}
            setTO_EMAIL={setC_EMAIL}
            USER_EMAIL={quote?.receivedInInbox}
            selectedQuote={quote}
            defaultOpen={defaultOpen["CustomerConversation"]}
            highlight={lastUpdatedThreadId && quote?.gmailThreadId &&
              (lastUpdatedThreadId === quote?.gmailThreadId ||
                String(lastUpdatedThreadId) === String(quote?.gmailThreadId))}
            onFullscreen={() => openFS("CustomerConversation")}
            isFullscreen={isFullscreen}
            layoutVariant="flat"
            fillParentHeight
            setRefetchTrigger={setRefetchTrigger}
            onParseWithAi={(_q, messageId) =>
              runAiParse({ messageId: messageId || undefined, force: true })
            }
            parseInFlight={aiParsing}
          />
        );
      case "AirlineConversation":
        return (
          <AirlineConversation
            my_saved_airlines={quote?.my_saved_airlines}
            openPanel={openPanel}
            setOpenPanel={setOpenPanel}
            USER_EMAIL={quote?.receivedInInbox}
            setPanelTriggerKey={setPanelTriggerKey}
            defaultOpen={defaultOpen["AirlineConversation"]}
            finalAirlineId={quote?.finalizedQuoteOption?.calculationDataForReference?.airline?.airlineId}
            highlightThreadId={lastUpdatedThreadId}
            setRefetchTrigger={setRefetchTrigger}
            setFqctTrigger={setFqctTrigger}
            panelTriggerKey={panelTriggerKey}
            onFullscreen={() => openFS("AirlineConversation")}
            isFullscreen={isFullscreen}
          />
        );
      case "ShipperConversation":
        return (
          <ShipperConversation
            shipperDetails={quote?.shipperDetails}
            openPanel={openPanel}
            setOpenPanel={setOpenPanel}
            panelTriggerKey={panelTriggerKey}
            disabled={
              !quote?.shipperDetails?.email && "Shipper Conversation not found"
            }
            defaultShipperEmail={S_EMAIL}
            USER_EMAIL={quote?.receivedInInbox}
            defaultOpen={defaultOpen["ShipperConversation"]}
            highlightThreadId={lastUpdatedThreadId}
            onFullscreen={() => openFS("ShipperConversation")}
            isFullscreen={isFullscreen}
            setRefetchTrigger={setRefetchTrigger}
          />
        );
      case "TransportConversation":
        return (
          <TransportConversation
            my_saved_agents={quote?.my_saved_agents}
            my_final_agent={quote?.finalAgent}
            openPanel={openPanel}
            setOpenPanel={setOpenPanel}
            setPanelTriggerKey={setPanelTriggerKey}
            USER_EMAIL={quote?.receivedInInbox}
            defaultOpen={defaultOpen["TransportConversation"]}
            finalAgentId={(quote?.my_saved_agents || []).filter(a => a._id == quote?.finalizedQuoteOption?.savedAgentId)[0]?.agentId}
            highlightThreadId={lastUpdatedThreadId}
            setRefetchTrigger={setRefetchTrigger}
            setFqctTrigger={setFqctTrigger}
            panelTriggerKey={panelTriggerKey}
            onFullscreen={() => openFS("TransportConversation")}
            isFullscreen={isFullscreen}
          />
        );
      case "FinalQuoteCostTable":
        return (
          <div data-tour="qd-cost-table" className="flex min-h-0 min-w-0 flex-1 flex-col">
            <FinalQuoteCostTable
              quoteId={quote?._id}
              disabled={finalQuoteDisabled}
              USER_EMAIL={quote?.receivedInInbox}
              quoteSource={quote?.source}
              defaultOpen={defaultOpen["FinalQuoteCostTable"]}
              refetchTrigger={refetchTrigger + fqctTrigger}
              setRefetchTrigger={setFqctTrigger}
              tableRefs={tableRefs}
              triggerPanel={triggerPanel}
              setItemRefs={setItemRefs}
              /* This lane is a ~530px column and the table is ~15 columns wide,
                 so the Margin cell was clipped off the right edge. The lane
                 knows that; the table cannot. Opened full-width once, and not
                 again if the operator closes it. */
              autoFullscreen
            />
          </div>
        );
      default:
        return null;
    }
  };
  // Panel keys are code identifiers, so de-camel-casing "TransportConversation"
  // reintroduced a name for the haulage partner that no other screen uses.
  const modalTitle =
    fullscreenPanel === "TransportConversation"
      ? `${TRANSPORTER} conversation`
      : fullscreenPanel?.replace(/([A-Z])/g, " $1").trim() || "Conversation";

  const routeLabel = useMemo(() => {
    const origin = quote?.containerDetails?.origin;
    const destination = quote?.containerDetails?.destination;
    if (!origin && !destination) return "Route pending";
    return [origin, destination].filter(Boolean).join(" -> ");
  }, [quote?.containerDetails?.origin, quote?.containerDetails?.destination]);

  const shipmentOrigin = quote?.containerDetails?.origin;
  const shipmentDestination = quote?.containerDetails?.destination;
  const customerInboxEmail =
    quote?.customerDetails?.email || quote?.receivedInInbox || "";

  // Which lane just received something, while the operator is reading another.
  const { unreadByLane } = useLaneUnread(quote, activeConversationTab);

  const conversationItems = useMemo(() => {
    if (!quote) return [];

    return [
      {
        key: "CustomerConversation",
        label: "Customer",
        icon: faUser,
        available: !!quote?.gmailThreadId,
        status: quote?.gmailThreadId ? "Live" : "Missing",
        detail: quote?.customerDetails?.name || quote?.customerDetails?.email || quote?.receivedInInbox || "No customer contact yet",
      },
      {
        key: "AirlineConversation",
        label: "Airlines",
        icon: faPlane,
        available: (quote?.my_saved_airlines?.length || 0) > 0,
        status: `${quote?.my_saved_airlines?.length || 0} saved`,
        detail:
          quote?.finalizedQuoteOption?.calculationDataForReference?.airline?.airlineId ||
          "Manage rate requests and responses from airline partners.",
      },
      {
        key: "TransportConversation",
        label: TRANSPORTER,
        icon: faTruck,
        available: (quote?.my_saved_agents?.length || 0) > 0,
        status: `${quote?.my_saved_agents?.length || 0} saved`,
        detail:
          quote?.finalAgent?.agentDetail?.name ||
          quote?.finalizedQuoteOption?.calculationDataForReference?.agent?.agentId ||
          "Coordinate transporter responses.",
      },
      {
        key: "ShipperConversation",
        label: "Shipper",
        icon: faBuilding,
        available: !!quote?.shipperDetails?.email,
        status: quote?.shipperDetails?.email ? "Connected" : "Missing",
        detail: quote?.shipperDetails?.companyName || quote?.shipperDetails?.email || "No shipper contact saved",
      },
      {
        key: "FinalQuoteCostTable",
        label: "Final Cost",
        icon: faCalculator,
        available: true,
        status: quote?.finalizedQuoteOption ? "Ready" : "Preview",
        detail: quote?.finalizedQuoteOption
          ? "Comparison ready for review"
          : "Preview airline and transport combinations",
      },
    ];
  }, [quote]);

  return (
    <div className="fk-qw relative flex min-h-0 h-full min-w-0 flex-col overflow-hidden" style={{ background: "transparent" }}>
      {/* Ambient aurora (design motion #1): drifting blur orbs + slow dashed
          flight arcs, behind the workspace. pointer-events-none, reduced-motion
          safe via the .fk-qw keyframe cap. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div style={{ position: "absolute", top: "-14%", left: "-8%", width: "44vw", height: "44vw", borderRadius: "50%", background: "radial-gradient(circle at 50% 50%, var(--blue) 0%, transparent 66%)", opacity: 0.13, filter: "blur(50px)", animation: "fkqw-drift-a 34s ease-in-out infinite" }} />
        <div style={{ position: "absolute", top: "24%", right: "-12%", width: "40vw", height: "40vw", borderRadius: "50%", background: "radial-gradient(circle at 50% 50%, var(--violet) 0%, transparent 66%)", opacity: 0.1, filter: "blur(56px)", animation: "fkqw-drift-b 42s ease-in-out infinite" }} />
        <div style={{ position: "absolute", bottom: "-18%", left: "34%", width: "38vw", height: "38vw", borderRadius: "50%", background: "radial-gradient(circle at 50% 50%, var(--emerald) 0%, transparent 68%)", opacity: 0.07, filter: "blur(60px)", animation: "fkqw-drift-c 48s ease-in-out infinite" }} />
        <div style={{ position: "absolute", top: "-30%", right: "6%", width: "70vw", height: "70vw", borderRadius: "50%", borderTop: "1.5px dashed var(--blue-line)", opacity: 0.25, animation: "fkqw-arc 120s linear infinite" }} />
        <div style={{ position: "absolute", bottom: "-46%", left: "-14%", width: "64vw", height: "64vw", borderRadius: "50%", borderTop: "1.5px dashed var(--violet-soft)", opacity: 0.4, animation: "fkqw-arc 150s linear infinite reverse" }} />
      </div>
      {/* The cap was 1800px, so anything wider than that spent the difference on
          empty gutters — ~100px a side on a 2000px screen, while the inspector
          sat capped at 400px with its values truncating.

          The gutter is symmetric again now that the copilot FAB is docked in the
          bottom-right corner. It used to float at the vertical middle of the
          right edge, which meant this page had to hold a 96px lane empty down
          its whole height just so the inspector did not end up underneath it. */}
      <div className="relative z-[1] mx-auto flex min-h-0 min-w-0 w-full max-w-[2160px] flex-1 flex-col px-3 py-3 sm:px-5 sm:py-4 lg:px-7 lg:py-4 xl:px-8 max-[960px]:px-2 max-[960px]:py-2">
        <div className="shrink-0">
          <StickyNote quoteId={id} />
        </div>
        {!quote ? (
          <div className="flex min-h-0 flex-1 flex-col">
            <QuoteDetailsSkeleton />
          </div>
        ) : (
          <motion.div
            key="qd-content"
            variants={reveal}
            initial="hidden"
            animate="show"
            className="flex min-h-0 flex-1 flex-col"
          >
            <section data-tour="qd-header" className="qw-card relative min-w-0 max-w-full shrink-0 overflow-hidden p-3 sm:p-4 xl:p-5 max-[960px]:p-2.5">
              {!quoteOverviewExpanded ? (
                <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5">
                    {/* Unified status rail: status dot + type + id in a single pill.
                        Flat surface — no gradient or inset shadow — so the row reads
                        as data, not as three competing chrome elements. */}
                    <div
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white py-0.5 pl-2 pr-2.5 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.7)]"
                      role="group"
                      aria-label="Quote identity"
                    >
                      <span
                        className={`relative inline-flex items-center gap-1.5 overflow-visible rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                          quote.isQueryApproved !== false
                            ? "text-emerald-700 dark:text-emerald-300"
                            : "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300"
                        }`}
                        style={
                          quote.isQueryApproved !== false
                            ? { background: "var(--emerald-soft)" }
                            : undefined
                        }
                        title={quote.isQueryApproved !== false ? "Quote is approved" : "Quote is flagged"}
                      >
                        {/* Design motion: a rubber stamp presses in on load and
                            an emerald ripple radiates out from the pill. */}
                        {quote.isQueryApproved !== false && (
                          <span
                            aria-hidden
                            style={{ position: "absolute", inset: 0, borderRadius: "6px", border: "1.5px solid var(--emerald)", opacity: 0.6, animation: "fkqw-ripple 1.4s ease-out .35s both" }}
                          />
                        )}
                        <FontAwesomeIcon
                          icon={quote.isQueryApproved !== false ? faStamp : faTimesCircle}
                          className="text-[10px]"
                          aria-hidden
                          style={quote.isQueryApproved !== false ? { animation: "fkqw-stamp .9s cubic-bezier(.2,.9,.25,1) both" } : undefined}
                        />
                        {quote.isQueryApproved !== false ? "Approved" : "Flagged"}
                      </span>
                      <span className="h-3 w-px bg-slate-200 dark:bg-[oklch(0.3_0.024_264)]" aria-hidden />
                      <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-600 dark:text-slate-300">
                        {quote?.type || "Untyped"}
                      </span>
                      <span className="h-3 w-px bg-slate-200 dark:bg-[oklch(0.3_0.024_264)]" aria-hidden />
                      <span
                        className="font-mono text-[10.5px] text-slate-500 dark:text-slate-400"
                        title={`Quote ID ${quote?._id || id}`}
                      >
                        #{quote?._id?.slice(-6) || String(id).slice(-6)}
                      </span>
                    </div>

                    {/* Route — design flight-board: bold amber airport codes with
                        a plane travelling a dashed amber line between them. */}
                    <div
                      className="inline-flex min-w-0 max-w-full items-center gap-3 rounded-full border border-slate-200 bg-white px-3.5 py-1 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.7)]"
                      title={routeLabel}
                    >
                      <span
                        className="min-w-0 truncate font-mono text-[13px] font-bold uppercase tracking-wide md:max-w-[7rem]"
                        style={{ color: "var(--amber)" }}
                      >
                        {shipmentOrigin || "—"}
                      </span>
                      {/* The plane used to fly this line every six seconds. A
                          travelling glyph over white content is the one motion
                          this codebase has already rejected once — it reads as an
                          attention-seeking arrow beside a route you are trying to
                          read — and the review asked for no animation here at
                          all. Parked at the destination end, it still says which
                          way the shipment goes. */}
                      <span aria-hidden className="relative h-4 w-24 shrink-0">
                        <span style={{ position: "absolute", top: "50%", left: "2px", right: "2px", borderTop: "1.25px dashed var(--amber)", opacity: 0.5 }} />
                        <span style={{ position: "absolute", top: "50%", right: 0, transform: "translateY(-50%)", color: "var(--amber)", lineHeight: 0 }}>
                          <PlaneGlyph size={13} strokeWidth={2} />
                        </span>
                      </span>
                      <span
                        className="min-w-0 truncate font-mono text-[13px] font-bold uppercase tracking-wide md:max-w-[7rem]"
                        style={{ color: "var(--amber)" }}
                      >
                        {shipmentDestination || "—"}
                      </span>
                    </div>

                    {customerInboxEmail ? (
                      <span
                        className="hidden min-w-0 max-w-[12rem] items-center gap-1.5 truncate rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10.5px] text-slate-500 lg:inline-flex xl:max-w-[18rem] dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.7)] dark:text-slate-400"
                        title={customerInboxEmail}
                      >
                        <FontAwesomeIcon icon={faEnvelope} className="text-[9px] text-slate-400" aria-hidden />
                        <span className="truncate">{customerInboxEmail}</span>
                      </span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => setQuoteOverviewExpanded(true)}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 hover:text-fk-accent dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.7)] dark:text-slate-300 dark:hover:bg-[oklch(0.26_0.028_264)]"
                    aria-expanded="false"
                    aria-controls="qd-quote-overview-panel"
                  >
                    <FontAwesomeIcon icon={faTableColumns} className="text-[10px] text-slate-400" aria-hidden />
                    {/* Says what is behind it — "Overview" never told anyone the stage rail was in there. */}
                    <span className="max-[380px]:sr-only">Stages</span>
                    <FontAwesomeIcon icon={faChevronDown} className="text-[9px]" aria-hidden />
                  </button>
                </div>
              ) : (
                <div className="min-w-0 space-y-3">
                  <div className="flex min-w-0 flex-col gap-2 sm:gap-3 xl:flex-row xl:items-center xl:justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${
                          quote.isQueryApproved !== false
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-red-50 text-red-600"
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={quote.isQueryApproved !== false ? faCheckCircle : faTimesCircle}
                          className="text-[10px]"
                        />
                        {quote.isQueryApproved !== false ? "Approved" : "False flagged"}
                      </span>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-600">
                        {quote?.type || "Untyped"}
                      </span>
                      <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-medium text-slate-500">
                        Quote ID: {quote?._id?.slice(-8) || id}
                      </span>
                    </div>

                    <div className="flex w-full min-w-0 flex-wrap items-center gap-2 xl:w-auto xl:max-w-[min(100%,56rem)] xl:justify-end">
                      <div
                        className="inline-flex max-w-full items-center gap-2 rounded-full border border-slate-200/80 bg-gradient-to-b from-white to-slate-50 dark:bg-none px-3 py-1 shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_4px_12px_-6px_rgba(15,23,42,0.10)] ring-1 ring-white/60 backdrop-blur-sm dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264/0.7)] dark:ring-white/5"
                        role="group"
                        aria-label="Shipment route"
                      >
                        <span className="shrink-0 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                          Route
                        </span>
                        {shipmentOrigin || shipmentDestination ? (
                          <>
                            <span className="font-mono text-[11px] font-semibold uppercase tracking-wide text-slate-900 dark:text-slate-100">
                              {shipmentOrigin || "—"}
                            </span>
                            <FontAwesomeIcon
                              icon={faArrowRight}
                              className="shrink-0 text-[10px] text-fk-accent dark:text-blue-400"
                              aria-hidden
                            />
                            <span className="font-mono text-[11px] font-semibold uppercase tracking-wide text-slate-900 dark:text-slate-100">
                              {shipmentDestination || "—"}
                            </span>
                          </>
                        ) : (
                          <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                            {routeLabel}
                          </span>
                        )}
                      </div>

                      <div
                        className="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full border border-slate-200/80 bg-gradient-to-b from-slate-50 to-white dark:bg-none px-3 py-1 shadow-[0_1px_0_rgba(255,255,255,0.9)_inset] ring-1 ring-white/50 backdrop-blur-sm dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264/0.7)] dark:ring-white/5"
                        role="group"
                        aria-label="Customer inbox"
                      >
                        <FontAwesomeIcon
                          icon={faEnvelope}
                          className="shrink-0 text-[10px] text-fk-accent dark:text-blue-400"
                        />
                        <span className="shrink-0 text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                          Inbox
                        </span>
                        <span
                          className="min-w-0 truncate text-[11px] font-medium text-slate-700 dark:text-slate-200"
                          title={customerInboxEmail || undefined}
                        >
                          {customerInboxEmail || "Customer email pending"}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => setQuoteOverviewExpanded(false)}
                        className="flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200/80 bg-gradient-to-b from-white to-slate-50 dark:bg-none px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_2px_6px_-3px_rgba(15,23,42,0.10)] transition-all hover:-translate-y-[1px] hover:bg-white hover:text-fk-accent hover:shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_6px_14px_-6px_rgba(37,99,235,0.22)] dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-300 dark:hover:bg-[oklch(0.26_0.028_264)] sm:text-[11px]"
                        aria-expanded="true"
                        aria-controls="qd-quote-overview-panel"
                      >
                        <span className="hidden sm:inline">Hide stages</span>
                        <FontAwesomeIcon icon={faChevronUp} className="text-[10px]" aria-hidden />
                      </button>
                    </div>
                  </div>

                  {/* Overview now shows the stage board full-width — the
                      workspace-control shortcuts live under the Workspace Lanes. */}
                  <div
                    id="qd-quote-overview-panel"
                    data-tour="qd-stage-bar"
                    className="min-w-0"
                  >
                    <HeaderProgressBar currentStage={currentStage} variant="board" />
                  </div>
                </div>
              )}
            </section>

            <div
              className={`mt-3 grid min-h-0 min-w-0 flex-1 grid-cols-1 gap-3 sm:mt-4 sm:gap-4 xl:items-stretch ${mainWorkspaceGridClass}`}
            >
              <aside className="flex h-full min-h-0 min-w-0 flex-col" aria-label="Workspace lanes">
                <PanelBoundary label="the workspace lanes" resetKey={quote?._id}>
                <div
                  data-tour="qd-workspace-lanes"
                  className={`qw-card relative flex min-h-0 flex-1 flex-col overflow-hidden ${
                    lanesCompact ? "p-2" : "p-2.5"
                  }`}
                >
                  <div
                    className={`relative z-[1] flex items-center gap-2 ${
                      lanesCompact ? "mb-1.5 flex-col" : "justify-between px-1"
                    }`}
                  >
                    {!lanesCompact ? (
                      <p className="qw-eyebrow">WORKSPACE LANES</p>
                    ) : null}
                    {lgMediaUp ? (
                      <button
                        type="button"
                        onClick={() => setLanesIconOnly((v) => !v)}
                        title={
                          lanesCompact
                            ? "Expand workspace lanes (show labels)"
                            : "Collapse lanes to icons"
                        }
                        aria-label={
                          lanesCompact
                            ? "Expand workspace lanes"
                            : "Collapse workspace lanes to icons"
                        }
                        className={`flex items-center justify-center rounded-lg border border-slate-200 bg-slate-50/70 p-1.5 text-slate-500 transition hover:bg-white hover:text-slate-800 hover:shadow-sm dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-400 dark:hover:bg-[oklch(0.26_0.028_264)] ${
                          lanesCompact ? "w-full" : "shrink-0"
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={lanesCompact ? faChevronRight : faChevronLeft}
                          className="text-[10px]"
                        />
                      </button>
                    ) : null}
                  </div>

                  {/* Controls live at the TOP (operator UX: shortcuts reachable
                      first), separated from the lanes by a soft divider. */}
                  {!lanesCompact && (
                    <div className="relative z-[1] mt-2 shrink-0 border-b border-slate-200/70 pb-2.5 dark:border-[oklch(0.33_0.022_264/0.6)]">
                      <p className="qw-eyebrow mb-1.5 px-1">CONTROLS</p>
                      {/* The Stage checklist button is gone: the checklist it
                          opened is now permanently visible at the foot of this
                          rail, so the control only offered a floating duplicate
                          of what is already on screen. */}
                      <div className="grid grid-cols-2 gap-1.5">
                        {navItemsData.map((item, i) => {
                          const meta = workspaceShortcutMeta[i];
                          const isDisabled = item.disabled(quote);
                          return (
                            <button
                              key={item.label}
                              type="button"
                              title={item.label}
                              onClick={() => !isDisabled && item.onClick(quote)}
                              disabled={isDisabled}
                              className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-left text-[11px] font-medium transition-colors ${
                                isDisabled
                                  ? "cursor-not-allowed border-slate-200/60 bg-white/60 text-slate-400 dark:border-[oklch(0.33_0.022_264/0.6)] dark:bg-[oklch(0.225_0.026_264/0.5)] dark:text-slate-600"
                                  : "border-transparent text-slate-700 hover:brightness-[0.97] dark:text-slate-200"
                              }`}
                              style={isDisabled ? undefined : { background: meta.soft }}
                            >
                              <FontAwesomeIcon
                                icon={meta.icon}
                                className="shrink-0 text-[11px]"
                                style={isDisabled ? undefined : { color: meta.color }}
                              />
                              <span className="truncate">{meta.short}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* flex-1: the lanes region stretches to the card footer so the
                      pipeline spine + travelling pulse run the FULL card height
                      (design), not just the five buttons. */}
                  {/* No longer flex-1: the spine used to be stretched down the
                      whole rail so the empty space below the lanes looked
                      intentional. The checklist occupies that space now, so the
                      spine ends where the lifecycle it draws ends. */}
                  <div className={`relative z-[1] ${lanesCompact ? "mt-1 space-y-1.5" : "mt-2 shrink-0 space-y-1.5"}`}>
                    {/* Pipeline spine (design motion: quote lifecycle) — scoped
                        to the lanes list so it runs Customer → Final Cost. */}
                    {!lanesCompact && (
                      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0">
                        <div style={{ position: "absolute", left: "21px", top: "12px", bottom: "12px", width: "2px", background: "linear-gradient(var(--emerald), var(--blue) 20%, var(--line) 36%)", borderRadius: "2px" }} />
                        {/* The spine stays; the dot that ran down it does not. */}
                      </div>
                    )}
                    {conversationItems.map((item) => (
                      <ConversationLaneButton
                        key={item.key}
                        item={item}
                        active={item.key === activeConversationTab}
                        unread={unreadByLane[item.key] || 0}
                        iconOnly={lanesCompact}
                        onClick={() => activateConversation(item.key)}
                        tourId={
                          item.key === "AirlineConversation"
                            ? "qd-airline-lane"
                            : item.key === "CustomerConversation"
                              ? "qd-customer-lane"
                              : item.key === "TransportConversation"
                                ? "qd-transport-lane"
                                : item.key === "ShipperConversation"
                                  ? "qd-shipper-lane"
                                  : item.key === "FinalQuoteCostTable"
                                    ? "qd-cost-lane"
                                    : undefined
                        }
                      />
                    ))}
                  </div>

                  {/* The rail's leftover height, spent on the one thing an
                      operator on this page needs next: what is still outstanding
                      for this stage. It used to be a floating dropdown behind a
                      button — "the stage stepper is collapsed by default, the
                      most useful orientation element is hidden" — while the
                      column below the lanes sat empty down to the footer.
                      StageChecklistDropdown already had an `inline` mode built
                      for exactly this, it had just never been used. */}
                  {!lanesCompact && quote?._id && (
                    <div className="relative z-[1] mt-3 min-h-0 flex-1 overflow-y-auto border-t border-slate-200/70 pt-2.5 dark:border-[oklch(0.33_0.022_264/0.6)]">
                      <p className="qw-eyebrow mb-1.5 px-1">THIS STAGE</p>
                      <StageChecklistDropdown
                        inline
                        quoteId={quote._id}
                        setCurrentStage={setCurrentStage}
                      />
                    </div>
                  )}

                  {/* Footer keeps ONLY the destructive action, tucked away from
                      the everyday controls (which now live at the top). */}
                  {!lanesCompact && (
                    <div className="relative z-[1] mt-auto shrink-0 pt-2">
                      {quote?.type === "query" && quote?.isQueryApproved !== false && (
                        <button
                          onClick={() => setShowConfirm(true)}
                          disabled={flagLoading}
                          className={`mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] font-medium text-red-700 transition dark:border-red-900/40 dark:bg-red-500/10 dark:text-red-400 ${
                            flagLoading ? "cursor-not-allowed opacity-60" : "hover:bg-red-100 dark:hover:bg-red-500/15"
                          }`}
                        >
                          <FontAwesomeIcon icon={faExclamationTriangle} className="text-[10px]" />
                          {flagLoading ? "Marking…" : "Mark quote as false"}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                </PanelBoundary>
              </aside>

              <div
                data-tour="qd-conversation"
                className="flex min-h-0 min-w-0 flex-1 flex-col space-y-3 overflow-x-hidden overflow-y-hidden sm:space-y-4"
              >
                <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                  <PanelBoundary label="this conversation" resetKey={`${activeConversationTab}:${quote?._id || ""}`}>
                    {renderPanel(activeConversationTab)}
                  </PanelBoundary>
                </div>
              </div>

              <aside
                data-tour="qd-right-panel"
                className="flex h-full min-h-0 min-w-0 flex-col max-xl:border-t max-xl:border-slate-200 max-xl:pt-3 dark:max-xl:border-[oklch(0.33_0.022_264)]"
              >
                <div className="relative flex min-h-0 flex-1 flex-col xl:sticky xl:top-4 xl:max-h-[min(calc(100dvh-2rem),100%)] xl:w-full xl:self-start">
                  <PanelBoundary label="the shipment inspector" resetKey={quote?._id}>
                  <QuoteDetails
                    quote={quote}
                    triggerPanel={triggerPanel}
                    C_EMAIL={C_EMAIL}
                    setS_EMAIL={setS_EMAIL}
                    defaultOpen={defaultOpen["QuoteDetails"]}
                    setRefetchTrigger={setRefetchTrigger}
                    currentStage={currentStage}
                    iconRailOnly={inspectorIconOnly && lgMediaUp}
                    onToggleInspectorRail={
                      lgMediaUp ? () => setInspectorIconOnly((v) => !v) : undefined
                    }
                    onExpandInspectorFromTab={
                      lgMediaUp ? () => setInspectorIconOnly(false) : undefined
                    }
                    containerInspectorEpoch={containerInspectorEpoch}
                    onReparse={
                      quote?.aiParsedAt ||
                      (quote?.type === "query" && quote?.source !== "manual")
                        ? handleAiParseClick
                        : undefined
                    }
                    reparseMode={quote?.aiParsedAt ? "rerun" : "first"}
                    reparseInFlight={aiParsing}
                  />
                  </PanelBoundary>
                  <AiParseOverlay
                    open={aiParsing}
                    onCancel={() => {
                      setAiParsing(false);
                      clearAiParseLock(id);
                    }}
                  />
                  <AiRerunConfirmDialog
                    open={reparseConfirmOpen}
                    editedCount={editedSinceParseCount}
                    onCancel={() => setReparseConfirmOpen(false)}
                    onConfirm={confirmReparse}
                  />
                </div>
              </aside>
            </div>
          </motion.div>
        )}

        {showConfirm && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm">
            <div className="p-6 rounded-xl w-[min(480px,92vw)] bg-white dark:bg-[oklch(0.225_0.026_264)] shadow-lg border border-slate-200 dark:border-[oklch(0.33_0.022_264)]">
              <h2 className="text-lg font-semibold mb-3 text-slate-900 dark:text-white">
                Confirmation Required
              </h2>
              <p className="mb-4 text-[13px] text-slate-600 dark:text-slate-400">
                Type <span className="font-semibold text-slate-900 dark:text-white">CONFIRM</span> below to proceed.
              </p>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={flagLoading}
                placeholder="Type CONFIRM"
                className="w-full rounded-xl px-4 py-2.5 mb-4 text-[13px] bg-white dark:bg-[oklch(0.26_0.028_264)] border border-slate-200 dark:border-[oklch(0.33_0.022_264)] text-slate-900 dark:text-white focus:border-fk-accent focus:ring-2 focus:ring-fk-accent/10 outline-none disabled:opacity-50"
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => { setShowConfirm(false); setInput(""); }}
                  className="px-4 py-2 rounded-xl text-[13px] font-medium border border-slate-200 dark:border-[oklch(0.33_0.022_264)] text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-[oklch(0.26_0.028_264)] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (input !== "CONFIRM") {
                      alert("You must type CONFIRM");
                      return;
                    }
                    try {
                      setFlagLoading(true);
                      const res = await falseFlagQuote({
                        token: loginToken,
                        quoteId: quote?._id || id,
                        confirmationText: "CONFIRM",
                        role: userLoggedIn?.role,
                      });
                      if (res?.error) {
                        showSwal("Error", "Failed to update quote status", 400);
                        return;
                      }
                      if (res?.quote) setQuote(res.quote);
                      setRefetchTrigger((k) => k + 1);
                      showSwal("Success", "Quote Flagged", 200);
                      setShowConfirm(false);
                      setInput("");
                    } catch (e) {
                      console.error(e);
                      alert("Server error while marking as false.");
                    } finally {
                      setFlagLoading(false);
                    }
                  }}
                  disabled={flagLoading}
                  className={`px-4 py-2 rounded-xl text-[13px] font-medium text-white ${flagLoading ? 'bg-red-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'} transition-colors`}
                >
                  {flagLoading ? "Processing..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        )}
        {fullscreenPanel && (
          <FullscreenModal
            title={modalTitle}
            onPrev={goPrev}
            onNext={goNext}
            onClose={closeFS}
          >
            {renderPanel(fullscreenPanel, true)}
          </FullscreenModal>
        )}
      </div>
    </div>
  );
}
